import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';

const testimonials = [
  {
    id: 1,
    name: 'Clinic Director',
    role: 'Director',
    company: 'Top Outpatient Treatment Center',
    specialty: 'Integrative Mental Health Services',
    location: 'Salem, Oregon, USA',
    quote: "We've worked with MDeRCM for over a year now, and they've truly become an extension of our own team. Their understanding of the behavioral health landscape and their ability to adapt to our specific workflow made a real difference in how smoothly our billing and operations run today.",
    highlight: "Their support on claims management and reconciliation has noticeably improved our cash flow, and their team's professionalism has been consistent throughout. We're now planning to expand their scope to include Medicaid and Commercial billing support as well as coding services.",
    endorsement: "MDeRCM's professionalism, efficiency, and commitment to quality have exceeded our expectations. We wholeheartedly endorse them as a reliable and high-quality vendor for healthcare service providers.",
    rating: 5,
    services: ['Claims Management', 'Cash Flow Optimization', 'Medicaid Billing'],
    icon: '🧠',
    datePublished: '2024-11-15'
  },
  {
    id: 2,
    name: 'COO',
    role: 'Chief Operating Officer',
    company: 'Behavioral Health Group Practice',
    specialty: 'Behavioral Health',
    location: 'Passaic, New Jersey',
    quote: "Just wanted to say how much we appreciate your partnership! Your responsiveness and clarity make a huge difference - we always get timely updates and quick resolutions whenever questions come up.",
    highlight: "We started with Utilization Review and then added Benefits Verification, and the experience has been excellent. We're now planning to move toward end-to-end billing with you, given how dependable your team has been.",
    endorsement: "Thank you for being so easy to work with and always going the extra mile!",
    rating: 5,
    services: ['Utilization Review', 'Benefits Verification', 'End-to-End Billing'],
    icon: '👨‍⚕️',
    datePublished: '2024-10-28'
  },
  {
    id: 3,
    name: 'Owner & President',
    role: 'President',
    company: 'Healthcare Billing Firm',
    specialty: 'Healthcare Revenue Cycle Management',
    location: 'Orlando, Florida',
    quote: "Just wanted to take a moment to say thank you both for the incredible support you've provided. Partnering with your team has been a great decision we've made. Everything from communication to execution has been seamless.",
    highlight: "I'm genuinely excited about the continued growth of our collaboration and appreciate the way you approach every challenge with such professionalism and care.",
    endorsement: "Thanks again for being such great partners.",
    rating: 5,
    services: ['Full RCM Services', 'Communication Excellence', 'Partnership Growth'],
    icon: '🤝',
    datePublished: '2024-09-12'
  },
  {
    id: 4,
    name: 'CEO',
    role: 'Chief Executive Officer',
    company: 'Multi-Location Dental Group',
    specialty: 'Dental Services',
    location: 'Southern California',
    quote: "We're a dental group based in Southern California, and we partnered with MDeRCM back in mid-2021. From day one, their professionalism stood out - clear communication, accurate reporting, and a genuine sense of ownership in every task.",
    highlight: "Over time, they've helped us streamline collections and minimize claim denials, which has had a direct impact on our bottom line. It's been a pleasure working with a team that's as dedicated and reliable as they are.",
    endorsement: "I'd gladly recommend MDeRCM to any dental or medical billing company seeking efficiency, cost savings, and a dependable partner.",
    rating: 5,
    services: ['Dental Billing', 'Collections', 'Denial Management'],
    icon: '🦷',
    datePublished: '2024-08-05'
  },
  {
    id: 5,
    name: 'Lead Dentist',
    role: 'Principal Dentist',
    company: 'Private Dental Practice',
    specialty: 'General Dentistry',
    location: 'Houston, Texas',
    quote: "I've been using MDeRCM's billing services for quite some time now, and I have to say, they've been nothing short of outstanding. The turnaround time, accuracy, and attention to detail have consistently exceeded expectations.",
    highlight: "Whenever we've needed special assistance or quick adjustments, their team has always been accommodating and proactive.",
    endorsement: "I'd strongly recommend your service to all colleagues.",
    rating: 5,
    services: ['Dental Billing', 'Quick Turnaround', 'Accuracy Excellence'],
    icon: '🦷',
    datePublished: '2024-07-18'
  }
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [slideHeight, setSlideHeight] = useState('auto');
  const slideRef = useRef(null);

  useEffect(() => {
    if (slideRef.current) {
      const height = slideRef.current.offsetHeight;
      setSlideHeight(height);
    }
  }, [currentIndex]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      let nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) nextIndex = testimonials.length - 1;
      if (nextIndex >= testimonials.length) nextIndex = 0;
      return nextIndex;
    });
  };

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  // Schema removed - now in public/index.html for server-side rendering

  return (
    <section
      className="testimonials-section"
      style={{
        padding: '100px 5%',
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#f8f9fa',
        position: 'relative',
        overflow: 'hidden',
      }}
      role="region"
      aria-label="Client testimonials and reviews"
    >
      {/* Schema removed - now in public/index.html for server-side rendering */}

      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
        backgroundImage: 'radial-gradient(circle, #0077b6 1px, transparent 1px)',
        backgroundSize: '30px 30px',
      }} />

      {/* Section Header */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: '60px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <motion.h2
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            color: '#0077b6',
            marginBottom: '15px',
            fontWeight: '700',
          }}
        >
          What Our Clients Say
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
            color: '#666',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6',
          }}
        >
          Real feedback from healthcare providers we've partnered with
        </motion.p>
      </div>

      {/* Carousel Container */}
      <div
        className="carousel-container"
        style={{
          position: 'relative',
          maxWidth: '900px',
          margin: '0 auto',
          padding: '0 60px',
        }}
      >
        {/* Previous Button */}
        <button
          onClick={() => paginate(-1)}
          style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: '#0077b6',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(0, 119, 182, 0.3)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#005f8f';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#0077b6';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
          aria-label="Previous testimonial"
        >
          ‹
        </button>

        {/* Next Button */}
        <button
          onClick={() => paginate(1)}
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: '#0077b6',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(0, 119, 182, 0.3)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#005f8f';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#0077b6';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
          aria-label="Next testimonial"
        >
          ›
        </button>

        {/* Slides Container */}
        <div
          style={{
            position: 'relative',
            minHeight: slideHeight,
            overflow: 'hidden',
          }}
        >
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={currentIndex}
              ref={slideRef}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              style={{
                position: 'absolute',
                width: '100%',
              }}
            >
              <div
                className="testimonial-card"
                itemScope
                itemType="https://schema.org/Review"
                style={{
                  backgroundColor: 'white',
                  padding: 'clamp(30px, 5vw, 50px)',
                  borderRadius: '20px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                }}
              >
                {/* Specialty Badge */}
                <div
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#FFF3E0',
                    color: '#F26522',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    marginBottom: '20px',
                  }}
                >
                  {testimonials[currentIndex].icon} {testimonials[currentIndex].specialty}
                </div>

                {/* Star Rating */}
                <div
                  itemProp="reviewRating"
                  itemScope
                  itemType="https://schema.org/Rating"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '20px',
                    fontSize: '24px',
                  }}
                >
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: '#FFD700' }}>★</span>
                  ))}
                  <meta itemProp="ratingValue" content={testimonials[currentIndex].rating} />
                  <meta itemProp="bestRating" content="5" />
                </div>

                {/* Quote */}
                <p
                  itemProp="reviewBody"
                  style={{
                    fontSize: '1.1rem',
                    lineHeight: '1.8',
                    color: '#333',
                    fontStyle: 'italic',
                    marginBottom: '20px',
                    textAlign: 'center',
                  }}
                >
                  "{testimonials[currentIndex].quote}"
                </p>

                {/* Highlight */}
                <div
                  className="testimonial-highlight"
                  style={{
                    backgroundColor: '#E8F5E9',
                    padding: '20px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    borderLeft: '4px solid #4CAF50',
                  }}
                >
                  <p
                    style={{
                      fontSize: '1rem',
                      lineHeight: '1.7',
                      color: '#2E7D32',
                      margin: 0,
                    }}
                  >
                    <strong>💡 Key Result:</strong> {testimonials[currentIndex].highlight}
                  </p>
                </div>

                {/* Endorsement */}
                <p
                  className="testimonial-endorsement"
                  style={{
                    fontSize: '1rem',
                    lineHeight: '1.7',
                    color: '#555',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    marginBottom: '25px',
                  }}
                >
                  "{testimonials[currentIndex].endorsement}"
                </p>

                {/* Services Tags */}
                <div
                  className="testimonial-services"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    justifyContent: 'center',
                    marginBottom: '25px',
                  }}
                >
                  {testimonials[currentIndex].services.map((service, idx) => (
                    <span
                      key={idx}
                      style={{
                        backgroundColor: '#E3F2FD',
                        color: '#0077b6',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                      }}
                    >
                      {service}
                    </span>
                  ))}
                </div>

                {/* Author Info */}
                <div
                  className="testimonial-author"
                  itemProp="author"
                  itemScope
                  itemType="https://schema.org/Person"
                  style={{
                    textAlign: 'center',
                    paddingTop: '20px',
                    borderTop: '1px solid #eee',
                  }}
                >
                  <p
                    itemProp="name"
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      color: '#0077b6',
                      margin: '0 0 5px 0',
                    }}
                  >
                    {testimonials[currentIndex].name}
                  </p>
                  <p
                    itemProp="jobTitle"
                    style={{
                      fontSize: '0.95rem',
                      color: '#666',
                      margin: '0 0 3px 0',
                    }}
                  >
                    {testimonials[currentIndex].role}
                  </p>
                  <p
                    style={{
                      fontSize: '0.9rem',
                      color: '#999',
                      margin: 0,
                    }}
                  >
                    {testimonials[currentIndex].company} • {testimonials[currentIndex].location}
                  </p>
                  <meta itemProp="datePublished" content={testimonials[currentIndex].datePublished} />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dots Navigation */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '30px',
          }}
        >
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              style={{
                width: currentIndex === index ? '30px' : '12px',
                height: '12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: currentIndex === index ? '#F26522' : '#ddd',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                if (currentIndex !== index) {
                  e.currentTarget.style.backgroundColor = '#999';
                }
              }}
              onMouseLeave={(e) => {
                if (currentIndex !== index) {
                  e.currentTarget.style.backgroundColor = '#ddd';
                }
              }}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        {/* Counter */}
        <div
          style={{
            textAlign: 'center',
            marginTop: '20px',
            fontSize: '14px',
            color: '#666',
            fontWeight: '600',
          }}
        >
          {currentIndex + 1} / {testimonials.length}
        </div>
      </div>

      {/* MOBILE-OPTIMIZED RESPONSIVE STYLES */}
      <style>
        {`
          @media (max-width: 768px) {
            .carousel-container {
              padding: 0 20px !important;
            }
            
            .carousel-container button[aria-label="Previous testimonial"],
            .carousel-container button[aria-label="Next testimonial"] {
              width: 40px !important;
              height: 40px !important;
              font-size: 20px !important;
            }
            
            .carousel-container button[aria-label="Previous testimonial"] {
              left: 5px !important;
            }
            
            .carousel-container button[aria-label="Next testimonial"] {
              right: 5px !important;
            }
          }
          
          @media (max-width: 480px) {
            .carousel-container button[aria-label="Previous testimonial"],
            .carousel-container button[aria-label="Next testimonial"] {
              width: 35px !important;
              height: 35px !important;
              font-size: 18px !important;
              opacity: 0.9;
            }
          }
        `}
      </style>
    </section>
  );
};

export default Testimonials;